import { getRailsClient, getIdpAdminClient } from "../../client";
import { snakeCase } from "../../utils/string";

const railsAdminBase = "/api/internal_api/v1/admin";

// Verified from GET /api/internal_api/v1/admin/levels/view_list
export interface AdminLevelListItem {
  id: number;
  name: string;
  description: string;
  rankName: string;
  image: string | null;
  viewType: string;
}

export interface AdminLevelsListResponse {
  belt?: AdminLevelListItem[];
  climb?: AdminLevelListItem[];
  level?: AdminLevelListItem[];
}

export const getLevelsList = async () => {
  try {
    const API = `${railsAdminBase}/levels/view_list`;
    const { data } = await getRailsClient().get<AdminLevelsListResponse>(API);
    return data;
  } catch (error) {
    console.error("Error fetching levels list:", error);
  }
};

export const setNewActiveLevel = async (levelData: {
  id: number;
  name: string;
  description: string;
  variant: string;
  mapId: number;
}) => {
  try {
    // TODO This aint the right API right now
    const API = `${railsAdminBase}/levels/${levelData.id}`;
    const { data } = await getRailsClient({ useToast: true }).put<AdminLevelListItem>(API, {
      level: levelData,
    });
    return data;
  } catch (error) {
    console.error("Error updating level data:", error);
  }
};

// Verified from GET /api/internal_api/v1/admin/api_keys
export interface ApiKey {
  id: number;
  name: string;
}

export const getApiKeys = async () => {
  try {
    const API = `${railsAdminBase}/api_keys`;
    const { data } = await getRailsClient().get<{ apiKeys: ApiKey[] }>(API);
    return data;
  } catch (error) {
    console.error("Error fetching API keys:", error);
  }
};

export const createApiKey = async (name: string, value: string) => {
  try {
    const API = `${railsAdminBase}/api_keys`;
    const { data } = await getRailsClient({ useToast: true }).post<{ apiKey: ApiKey }>(API, {
      name,
      value,
    });
    return data;
  } catch (error) {
    console.error("Error creating API key:", error);
  }
};

export const deleteApiKey = async (keyId: number) => {
  try {
    const API = `${railsAdminBase}/api_keys/${keyId}`;
    const { data } = await getRailsClient().delete<null>(API);
    return data;
  } catch (error) {
    console.error("Error deleting API key:", error);
  }
};

export const getIdpSettings = async (tenantName: string, idpUuid: string) => {
  const idpAdminClient = getIdpAdminClient();
  return await idpAdminClient.getTenantIdpSettings(tenantName, idpUuid);
};

export const getTenantIdp = async (tenantName: string) => {
  const idpAdminClient = getIdpAdminClient();
  return await idpAdminClient.getTenantPrimaryIdp(tenantName);
};

export const updateTenantIdp = async (
  tenantName: string,
  idp: { metadata: string; ssoUrl: string; uuid: string; friendlyName: string }
) => {
  const idpAdminClient = getIdpAdminClient();
  return await idpAdminClient.updateTenantIdp({ tenantName, idp });
};

export const updateTenantSettings = async (tenantIdpSettings: {
  tenant?: string;
  idpUuid?: string;
  enabled?: boolean;
  autoProvision?: boolean;
}) => {
  const idpAdminClient = getIdpAdminClient();
  return await idpAdminClient.updateTenantSettings(tenantIdpSettings);
};

export const toggleAdminSetting = async (name: string, enabled: boolean) => {
  const setting = { name: snakeCase(name) };
  const API = `${railsAdminBase}/settings/${enabled ? "activate" : "disable"}`;
  return await getRailsClient({ useToast: true }).put<null>(API, { setting });
};

export const setAdminSettingValue = async (
  name: string,
  value: string | boolean,
  postURL: string = "set"
) => {
  const setting = { name: snakeCase(name), value };
  const API = `${railsAdminBase}/settings/${postURL}`;
  return await getRailsClient({ useToast: true }).put<null>(API, { setting });
};

export const setAdminSettings = async (
  updatedSettings: { name: string; value: string | boolean },
  postURL: string = ""
) => {
  try {
    let API = `${railsAdminBase}/settings/`;
    const setting: { name: string; value?: string | boolean } = {
      name: snakeCase(updatedSettings.name),
    };

    if (postURL) {
      API += postURL;
      setting.value = updatedSettings.value;
    } else if (!updatedSettings.value) {
      API += "disable";
    } else {
      API += "activate";
    }

    const data = await getRailsClient({ useToast: true }).put<null>(API, { setting });
    return data;
  } catch (error) {
    console.error("Error updating settings:", error);
    throw error;
  }
};

export const setAdminInfos = async (info: Record<string, string>) => {
  try {
    const API = `${railsAdminBase}/infos/set`;
    const data = await getRailsClient({ useToast: true }).put<null>(API, { info });
    return data;
  } catch (error) {
    console.error("Error updating admin infos:", error);
  }
};

export const setLevelSettings = async (level: {
  id: number;
  description: string;
  name: string;
  variant: string;
  mapId: number;
}) => {
  try {
    const API = `${railsAdminBase}/levels/${level.id}`;
    const data = await getRailsClient({ useToast: true }).put<AdminLevelListItem>(API, level);
    return data;
  } catch (error) {
    console.error("Error updating level settings:", error);
  }
};
